import React, { useMemo, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Badge } from '../ui/badge.tsx';
import { ChevronDown, ChevronRight, Plus, Search } from 'lucide-react';

// Formula presets organized by category
const STOCK_FORMULA_PRESETS = {
  Valuation: [
    {
      id: 'pe_ratio',
      name: 'PE Ratio',
      description: 'Price-to-earnings ratio',
      formula: 'price / eps.ttm(0)',
      better: 'lower'
    },
    {
      id: 'pb_ratio',
      name: 'PB Ratio',
      description: 'Price-to-book ratio',
      formula: 'price / book_value_per_share.mrq(0)',
      better: 'lower'
    },
    {
      id: 'ps_ratio',
      name: 'P/S Ratio',
      description: 'Price-to-sales ratio',
      formula: 'price / (revenue.ttm(0) / shares.mrq(0))',
      better: 'lower'
    },
    {
      id: 'pcf_ratio',
      name: 'P/CF Ratio',
      description: 'Price-to-cash flow ratio',
      formula: 'price / (ocf.ttm(0) / shares.mrq(0))',
      better: 'lower'
    },
    {
      id: 'pfcf_ratio',
      name: 'P/FCF Ratio',
      description: 'Price-to-free cash flow ratio',
      formula: 'price / (fcf.ttm(0) / shares.mrq(0))',
      better: 'lower'
    },
    {
      id: 'ev_ebitda_approx',
      name: 'EV/EBITDA (Approx)',
      description: 'Enterprise value to EBITDA using market cap + debt - cash',
      formula: '(price * shares.mrq(0) + totalDebt.mrq(0) - cashEq.mrq(0)) / ebitda.ttm(0)',
      better: 'lower'
    },
    {
      id: 'net_cash_to_market_cap',
      name: 'Net Cash / Market Cap',
      description: 'Net cash (cash - debt) relative to market cap',
      formula: '(cashEq.mrq(0) - totalDebt.mrq(0)) / (price * shares.mrq(0))',
      better: 'higher'
    },
    {
      id: 'earnings_yield',
      name: 'Earnings Yield',
      description: 'Inverse of P/E ratio',
      formula: 'eps.ttm(0) / price',
      better: 'higher'
    },
    {
      id: 'fcf_yield',
      name: 'FCF Yield',
      description: 'Free cash flow yield',
      formula: '(fcf.ttm(0) / shares.mrq(0)) / price',
      better: 'higher'
    },
    {
      id: 'dividend_yield',
      name: 'Dividend Yield',
      description: 'Annual dividends per share / price',
      formula: 'dps.ttm(0) / price',
      better: 'higher'
    }
  ],
  Profitability: [
    {
      id: 'roe',
      name: 'ROE',
      description: 'Return on equity',
      formula: 'roe.ttm(0)',
      better: 'higher'
    },
    {
      id: 'roa',
      name: 'ROA',
      description: 'Return on assets',
      formula: 'roa.ttm(0)',
      better: 'higher'
    },
    {
      id: 'roic',
      name: 'ROIC',
      description: 'Return on invested capital',
      formula: 'roic.ttm(0)',
      better: 'higher'
    },
    {
      id: 'roic_trend',
      name: 'ROIC Trend',
      description: 'Change in ROIC vs. ~1 year ago',
      formula: 'roic.ttm(0) - roic.ttm(4)',
      better: 'higher'
    },
    {
      id: 'gross_margin',
      name: 'Gross Margin',
      description: 'Gross profit margin',
      formula: 'grossMargin.ttm(0)',
      better: 'higher'
    },
    {
      id: 'operating_margin',
      name: 'Operating Margin',
      description: 'Operating profit margin',
      formula: 'operatingMargin.ttm(0)',
      better: 'higher'
    },
    {
      id: 'operating_margin_expansion',
      name: 'Operating Margin Expansion',
      description: 'Change in operating margin vs. ~1 year ago',
      formula: 'operatingMargin.ttm(0) - operatingMargin.ttm(4)',
      better: 'higher'
    },
    {
      id: 'net_margin',
      name: 'Net Margin',
      description: 'Net profit margin',
      formula: 'netMargin.ttm(0)',
      better: 'higher'
    },
    {
      id: 'fcf_margin',
      name: 'FCF Margin',
      description: 'Free cash flow as a share of revenue',
      formula: 'fcf.ttm(0) / revenue.ttm(0)',
      better: 'higher'
    },
    {
      id: 'income_quality',
      name: 'Income Quality',
      description: 'Cash flow to net income ratio',
      formula: 'incomeQuality.ttm(0)',
      better: 'higher'
    }
  ],
  Growth: [
    {
      id: 'revenue_growth',
      name: 'Revenue Growth',
      description: 'Year-over-year revenue growth',
      formula: '(revenue.ttm(0) - revenue.ttm(4)) / revenue.ttm(4)',
      better: 'higher'
    },
    {
      id: 'eps_growth',
      name: 'EPS Growth',
      description: 'Year-over-year EPS growth',
      formula: '(eps.ttm(0) - eps.ttm(4)) / eps.ttm(4)',
      better: 'higher'
    },
    {
      id: 'dividend_growth',
      name: 'Dividend Growth',
      description: 'Year-over-year dividend per share growth',
      formula: '(dps.ttm(0) - dps.ttm(4)) / dps.ttm(4)',
      better: 'higher'
    }
  ],
  Leverage: [
    {
      id: 'debt_to_equity',
      name: 'Debt-to-Equity',
      description: 'Total debt to shareholders equity',
      formula: 'debtToEquity.mrq(0)',
      better: 'lower'
    },
    {
      id: 'debt_to_assets',
      name: 'Debt-to-Assets',
      description: 'Total debt to total assets',
      formula: 'debtToAssets.mrq(0)',
      better: 'lower'
    },
    {
      id: 'net_debt_to_ebitda',
      name: 'Net Debt/EBITDA',
      description: 'Net debt to EBITDA ratio',
      formula: 'netDebtToEBITDA.ttm(0)',
      better: 'lower'
    }
  ],
  Liquidity: [
    {
      id: 'current_ratio',
      name: 'Current Ratio',
      description: 'Current assets to current liabilities',
      formula: 'currentRatio.mrq(0)',
      better: 'higher'
    },
    {
      id: 'quick_ratio',
      name: 'Quick Ratio',
      description: 'Quick assets to current liabilities',
      formula: 'quickRatio.mrq(0)',
      better: 'higher'
    },
    {
      id: 'cash_ratio',
      name: 'Cash Ratio',
      description: 'Cash to current liabilities',
      formula: 'cashRatio.mrq(0)',
      better: 'higher'
    }
  ],
  Efficiency: [
    {
      id: 'asset_turnover',
      name: 'Asset Turnover',
      description: 'Revenue per dollar of assets',
      formula: 'revenue.ttm(0) / ((totalAssets.mrq(0) + totalAssets.mrq(4)) / 2)',
      better: 'higher'
    },
    {
      id: 'receivables_turnover',
      name: 'Receivables Turnover',
      description: 'How quickly receivables are collected',
      formula: 'receivablesTurnover.ttm(0)',
      better: 'higher'
    },
    {
      id: 'inventory_turnover',
      name: 'Inventory Turnover',
      description: 'How quickly inventory is sold',
      formula: 'inventoryTurnover.ttm(0)',
      better: 'higher'
    },
    {
      id: 'days_sales_outstanding',
      name: 'Days Sales Outstanding',
      description: 'Days to collect receivables',
      formula: 'daysSalesOutstanding.ttm(0)',
      better: 'lower'
    }
  ],
  Technical: [
    {
      id: 'sma_200',
      name: '200-Day SMA',
      description: 'Trailing 200-day simple moving average of price',
      formula: 'sma(200)',
      better: 'higher'
    },
    {
      id: 'price_to_sma_200',
      name: 'Price / 200-Day SMA',
      description: 'Price relative to the 200-day SMA',
      formula: 'price / sma(200)',
      better: 'higher'
    },
    {
      id: 'sma_50_200_crossover',
      name: '50/200 SMA Crossover',
      description: '50-day SMA vs 200-day SMA',
      formula: 'sma(50) / sma(200) - 1',
      better: 'higher'
    },
    {
      id: 'momentum_12m',
      name: '12M Momentum',
      description: 'Price return over the past ~12 months',
      formula: 'momentum(252)',
      better: 'higher'
    },
    {
      id: 'momentum_12m_1m',
      name: '12M-1M Momentum',
      description: 'Price return from 12 months ago to 1 month ago',
      formula: 'momentum(252, 21)',
      better: 'higher'
    },
    {
      id: 'volatility_60d',
      name: '60D Volatility',
      description: 'Std dev of daily returns over the past ~60 days',
      formula: 'volatility(60)',
      better: 'lower'
    },
    {
      id: 'high_distance_52w',
      name: '52-Week High Distance',
      description: 'Distance below the 52-week high',
      formula: '1 - price / rolling_max(252)',
      better: 'lower'
    }
  ],
  Logic: [
    {
      id: 'if_trend_filter',
      name: 'IF Trend Filter',
      description: 'Binary trend filter using IF',
      formula: 'if(price > sma(200), 1, 0)',
      better: 'higher'
    },
    {
      id: 'btc_context_momentum',
      name: 'BTC Momentum (Context)',
      description: 'Use crypto context inside a stock formula',
      formula: 'context(CRYPTO:BTCUSD, momentum(252))',
      better: 'higher'
    }
  ],
  Earnings: [
    {
      id: 'earnings_reaction',
      name: 'Latest Earnings Reaction',
      description: 'Price move around the most recent earnings announcement (day after vs. day before)',
      formula: 'price(date_of_last_earnings_announcement + 1) / price(date_of_last_earnings_announcement - 1) - 1',
      better: 'higher'
    }
  ],
  Events: [
    {
      id: 'days_since_btc_halving',
      name: 'Days Since BTC Halving',
      description: 'Days since the most recent Bitcoin halving',
      formula: 'days_since(BITCOIN_HALVING)',
      better: 'lower'
    }
  ]
};

const MULTI_ASSET_PRESETS = {
  Trend: [
    {
      id: 'sma_20',
      name: '20-Day SMA',
      description: 'Trailing 20-day simple moving average of price',
      formula: 'sma(20)',
      better: 'higher'
    },
    {
      id: 'sma_50',
      name: '50-Day SMA',
      description: 'Trailing 50-day simple moving average of price',
      formula: 'sma(50)',
      better: 'higher'
    },
    {
      id: 'sma_200',
      name: '200-Day SMA',
      description: 'Trailing 200-day simple moving average of price',
      formula: 'sma(200)',
      better: 'higher'
    },
    {
      id: 'price_to_sma_50',
      name: 'Price / 50-Day SMA',
      description: 'Price relative to the 50-day SMA',
      formula: 'price / sma(50)',
      better: 'higher'
    },
    {
      id: 'price_to_sma_200',
      name: 'Price / 200-Day SMA',
      description: 'Price relative to the 200-day SMA',
      formula: 'price / sma(200)',
      better: 'higher'
    },
    {
      id: 'sma_50_200_crossover',
      name: '50/200 SMA Crossover',
      description: '50-day SMA vs 200-day SMA',
      formula: 'sma(50) / sma(200) - 1',
      better: 'higher'
    }
  ],
  Momentum: [
    {
      id: 'momentum_1m',
      name: '1M Momentum',
      description: 'Price return over the past ~1 month',
      formula: 'momentum(21)',
      better: 'higher'
    },
    {
      id: 'momentum_3m',
      name: '3M Momentum',
      description: 'Price return over the past ~3 months',
      formula: 'momentum(63)',
      better: 'higher'
    },
    {
      id: 'momentum_6m',
      name: '6M Momentum',
      description: 'Price return over the past ~6 months',
      formula: 'momentum(126)',
      better: 'higher'
    },
    {
      id: 'momentum_12m',
      name: '12M Momentum',
      description: 'Price return over the past ~12 months',
      formula: 'momentum(252)',
      better: 'higher'
    },
    {
      id: 'momentum_12m_1m',
      name: '12M-1M Momentum',
      description: 'Price return from 12 months ago to 1 month ago',
      formula: 'momentum(252, 21)',
      better: 'higher'
    }
  ],
  Risk: [
    {
      id: 'volatility_30d',
      name: '30D Volatility',
      description: 'Std dev of daily returns over the past ~30 days',
      formula: 'volatility(30)',
      better: 'lower'
    },
    {
      id: 'volatility_60d',
      name: '60D Volatility',
      description: 'Std dev of daily returns over the past ~60 days',
      formula: 'volatility(60)',
      better: 'lower'
    },
    {
      id: 'volatility_90d',
      name: '90D Volatility',
      description: 'Std dev of daily returns over the past ~90 days',
      formula: 'volatility(90)',
      better: 'lower'
    }
  ],
  Levels: [
    {
      id: 'price',
      name: 'Last Price',
      description: 'Most recent price',
      formula: 'price',
      better: 'higher'
    },
    {
      id: 'rolling_high_252',
      name: '52-Week High',
      description: 'Rolling 52-week high',
      formula: 'rolling_max(252)',
      better: 'higher'
    },
    {
      id: 'distance_52w_high',
      name: 'Distance to 52-Week High',
      description: 'Distance below the 52-week high',
      formula: '1 - price / rolling_max(252)',
      better: 'lower'
    }
  ],
  Events: [
    {
      id: 'days_since_btc_halving',
      name: 'Days Since BTC Halving',
      description: 'Days since the most recent Bitcoin halving',
      formula: 'days_since(BITCOIN_HALVING)',
      better: 'lower'
    }
  ]
};

const resolvePresetCatalog = (assetClass) => {
  const normalized = String(assetClass || '').toLowerCase().trim();
  if (normalized === 'stocks') {
    return STOCK_FORMULA_PRESETS;
  }
  if (['etfs', 'crypto', 'commodities'].includes(normalized)) {
    return MULTI_ASSET_PRESETS;
  }
  return STOCK_FORMULA_PRESETS;
};

const FormulaLibrary = ({ assetClass = 'stocks', onSelectPreset, onboardingStep = 4, onOnboardingComplete }) => {
  const presetsByCategory = useMemo(() => resolvePresetCatalog(assetClass), [assetClass]);
  const presetCategories = useMemo(
    () => Object.keys(presetsByCategory),
    [presetsByCategory]
  );
  const presetCategoriesKey = useMemo(
    () => presetCategories.join('|'),
    [presetCategories]
  );
  const [expandedCategories, setExpandedCategories] = useState(() => (
    presetCategories.reduce((acc, category) => {
      acc[category] = false;
      return acc;
    }, {})
  ));
  const [searchQuery, setSearchQuery] = useState('');
  const [onboardingCategory, setOnboardingCategory] = useState(null);

  useEffect(() => {
    setExpandedCategories((prev) => {
      const next = {};
      presetCategories.forEach((category) => {
        next[category] = prev[category] ?? false;
      });
      return next;
    });
  }, [presetCategories, presetCategoriesKey]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));

    // If onboarding step 0 (blinking category), progress to step 1
    if (onboardingStep === 0 && !expandedCategories[category]) {
      setOnboardingCategory(category);
      if (onOnboardingComplete) {
        onOnboardingComplete(1);
      }
    }
  };

  const handlePresetClick = (preset) => {
    onSelectPreset(preset);
  };

  // Filter formulas based on search query
  const getFilteredPresets = () => {
    if (!searchQuery.trim()) {
      return presetsByCategory;
    }

    const query = searchQuery.toLowerCase();
    const filtered = {};

    Object.entries(presetsByCategory).forEach(([category, presets]) => {
      const matchingPresets = presets.filter(preset =>
        preset.name.toLowerCase().includes(query) ||
        preset.description.toLowerCase().includes(query) ||
        preset.formula.toLowerCase().includes(query)
      );

      if (matchingPresets.length > 0) {
        filtered[category] = matchingPresets;
      }
    });

    return filtered;
  };

  const filteredPresets = getFilteredPresets();
  const isSearching = searchQuery.trim().length > 0;

  // Get the first category for onboarding (Valuation if available)
  const firstCategory = Object.keys(filteredPresets)[0] || 'Valuation';

  const getCategoryColor = (category) => {
    const colors = {
      Valuation: 'bg-blue-50 border-blue-200 text-blue-700',
      Profitability: 'bg-green-50 border-green-200 text-green-700',
      Growth: 'bg-purple-50 border-purple-200 text-purple-700',
      Leverage: 'bg-orange-50 border-orange-200 text-orange-700',
      Liquidity: 'bg-cyan-50 border-cyan-200 text-cyan-700',
      Efficiency: 'bg-pink-50 border-pink-200 text-pink-700',
      Logic: 'bg-indigo-50 border-indigo-200 text-indigo-700',
      Events: 'bg-amber-50 border-amber-200 text-amber-700',
      Trend: 'bg-blue-50 border-blue-200 text-blue-700',
      Momentum: 'bg-indigo-50 border-indigo-200 text-indigo-700',
      Risk: 'bg-red-50 border-red-200 text-red-700',
      Levels: 'bg-emerald-50 border-emerald-200 text-emerald-700'
    };
    return colors[category] || 'bg-gray-50 border-gray-200 text-gray-700';
  };

  const getBetterBadge = (better) => {
    if (better === 'higher') {
      return <Badge className="bg-green-100 text-green-800 text-xs">↑ Higher Better</Badge>;
    } else if (better === 'lower') {
      return <Badge className="bg-red-100 text-red-800 text-xs">↓ Lower Better</Badge>;
    }
    return null;
  };

  return (
    <>
      <style>{`
        @keyframes onboarding-blink {
          0%, 100% {
            background-color: transparent;
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
          50% {
            background-color: rgba(59, 130, 246, 0.1);
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
          }
        }
        .onboarding-blink {
          animation: onboarding-blink 1.5s ease-in-out infinite;
        }
      `}</style>
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Formula Presets</CardTitle>
          <p className="text-sm text-gray-600">Click to add a preset formula</p>

        {/* Search Bar */}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search formulas..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {Object.keys(filteredPresets).length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            No formulas found matching "{searchQuery}"
          </div>
        ) : (
          <div className="space-y-1">
            {Object.entries(filteredPresets).map(([category, presets]) => (
            <div key={category} className="border-b last:border-b-0">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category)}
                className={`w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors ${
                  onboardingStep === 0 && category === firstCategory && !isSearching ? 'onboarding-blink' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  {expandedCategories[category] ? (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-500" />
                  )}
                  <span className="font-semibold text-gray-900">{category}</span>
                  <Badge variant="secondary" className="text-xs">
                    {presets.length}
                  </Badge>
                </div>
              </button>

              {/* Category Items */}
              {(expandedCategories[category] || isSearching) && (
                <div className="pb-2">
                  {presets.map((preset, index) => (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetClick(preset)}
                      className={`w-full text-left px-6 py-2 hover:bg-gray-50 transition-colors border-l-2 ${
                        getCategoryColor(category).includes('blue') ? 'border-blue-400 hover:border-blue-500' :
                        getCategoryColor(category).includes('green') ? 'border-green-400 hover:border-green-500' :
                        getCategoryColor(category).includes('purple') ? 'border-purple-400 hover:border-purple-500' :
                        getCategoryColor(category).includes('orange') ? 'border-orange-400 hover:border-orange-500' :
                        getCategoryColor(category).includes('cyan') ? 'border-cyan-400 hover:border-cyan-500' :
                        'border-pink-400 hover:border-pink-500'
                      } border-transparent ${
                        onboardingStep === 1 && category === onboardingCategory && index === 0 && !isSearching ? 'onboarding-blink' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900">{preset.name}</span>
                            {getBetterBadge(preset.better)}
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5">{preset.description}</p>
                          <code className="text-xs text-gray-500 bg-gray-50 px-1 py-0.5 rounded mt-1 inline-block">
                            {preset.formula}
                          </code>
                        </div>
                        <Plus className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default FormulaLibrary;
